// THE METALS — one surface per metal, because eleven of them used to wear Iron's.
//
// Until this file existed the metal branch of the dispatch knew eight names (Iron, Gold, Copper, Silver and four ores)
// and sent every other metal to Iron's cache slot. Aluminum, Titanium, Steel and eight more therefore wore Iron's rusty
// rectangles, and because that one slot baked the hex of whichever of the twelve a session drew first, even Iron's own
// colour depended on the order a player met them in. Each metal here has its own generator and its own slot
// (METAL_TEXTURES in texture-dispatch.js), so no metal can change how another one looks.
//
// The rules texture-materials.js states in its header hold here too, for the reasons given there:
//   * 1024x1024, RepeatWrapping, and it TILES: every feature near an edge is drawn again on the far side (drawWrapped,
//     drawWrappedBand, whole-cycle sines, lattices whose jitter is periodic in the tile).
//   * DETERMINISTIC, and keyed by NAME. Each generator seeds from nameSeed('<its metal>') and draws through hash01, and
//     none of them reads the hex it is handed, so the canvas is a pure function of the material: the same on every
//     client, every session, whether the kube or its bit is the first thing to reach the slot.
//   * NEUTRAL: white and greys (Titanium's oxide colours are the one exception, and they are the point of it), because
//     applyMaterial multiplies the canvas by the kube's hex. These are pale metals, Sodium #E6E6E6 and Magnesium #E0E0E0
//     among them, and a canvas with the hex baked in squares it into a dull grey. That is what the iron lattice was
//     doing to all eleven.
//   * CHEAP: paths, gradients and (for Cobalt) three small sprites, no per-pixel pass. Each bakes once, and
//     TextureEngine ships it at 512 (TEX_PX), like every other slot added after the first twenty-five.
//
// WHAT TELLS THEM APART. Ten of the twelve are grey, so the pattern has to carry the identity on its own, at arm's
// length and at the 34px of a hotbar icon, which shows only a quarter of the tile. Each generator is built around the
// ONE feature that says its name, and no two share a structure:
//   Aluminum          rolled and brushed sheet: a dense one-way hairline grain under a soft sheen band
//   Calcium           dull metal under a blotchy, chalk-white oxide crust with fine crazing
//   Sodium            a knife-cut lump: bright, waxy, straight-edged cut faces with drag lines, set in a grey crust
//   Manganese         brittle electrolytic flake: plates split by jagged cracks, a granular glitter, black oxide
//   Titanium          bead-blasted matte carrying the straw-to-blue heat tint only titanium takes
//   Chromium          a mirror: a hard, wavy horizon reflected in the surface, bright sky over dark ground
//   Magnesium         a cast ingot's feathery six-fold dendrites under a dull oxide film
//   Cobalt            electrodeposited nodules, packed like grapes, each with its own specular point
//   Zirconium         polished crystal grains, each flashing its own brightness, with twin striations
//   Steel             blue-black mill scale, ground back to bright metal in patches with grinder swirls
//   Structural Alloy  a machined isogrid: triangular pockets between ribs, with a boss at every node
// The light comes from the upper left in every one, as it does everywhere else in the texture library, so a wall
// built from several metals still agrees about where the sun is.
//
// The on-world voxel material has no specular term at all (see createArmorPlateTexture), so a metal's shine exists
// only if it is painted here. That is why the chrome paints a reflection and the cobalt a highlight on every nodule.
import * as THREE from '/lib/three/three.module.min.js';   // REAL path, not the bare specifier — must resolve on Safari 15.6
import { hash01, nameSeed } from './texture-helpers.js';
import { drawWrapped, drawWrappedBand, iceBlobPath, iceGrainField, icePools } from './texture-materials.js';

const SIZE = 1024;
const TAU = Math.PI * 2;

function surface() {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = SIZE;
    return { canvas, ctx: canvas.getContext('2d') };
}
function asTexture(canvas) {
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}
// Integer channels and a clamped alpha only, so no engine this game still supports (Safari 15.6 among them) can refuse
// one: a colour the canvas refuses is silently dropped rather than reported (see blendColors in texture-helpers.js).
const grey = (v) => { const c = Math.round(Math.max(0, Math.min(255, v))); return 'rgb(' + c + ',' + c + ',' + c + ')'; };
const greyA = (v, a) => { const c = Math.round(Math.max(0, Math.min(255, v))); return 'rgba(' + c + ',' + c + ',' + c + ',' + Math.max(0, Math.min(1, a)).toFixed(3) + ')'; };
const rgbA = (r, g, b, a) => 'rgba(' + Math.round(r) + ',' + Math.round(g) + ',' + Math.round(b) + ',' + Math.max(0, Math.min(1, a)).toFixed(3) + ')';

// Full-width hairlines running one way. Each wobbles by a WHOLE number of cycles, so it leaves the right edge at the
// height it entered the left, and one near the top or bottom is drawn again across that edge. Heights are hashed
// rather than evenly spaced: an even comb reads as printed stripes, not as a finish.
function hairlines(ctx, seed, salt, count, light, dark, aLo, aHi, wLo, wHi, ampMax, step) {
    for (let i = 0; i < count; i++) {
        const y = hash01(i + seed, salt + 1) * SIZE;
        const cyc = 1 + Math.floor(hash01(i + seed, salt + 2) * 3);
        const amp = hash01(i + seed, salt + 3) * ampMax;
        const ph = hash01(i + seed, salt + 4) * TAU;
        const w = wLo + hash01(i, seed + salt + 7) * (wHi - wLo);
        ctx.globalAlpha = aLo + hash01(i, seed + salt + 5) * (aHi - aLo);
        ctx.strokeStyle = hash01(i, seed + salt + 6) > 0.5 ? light : dark;
        ctx.lineWidth = w;
        for (let rep = -1; rep <= 1; rep++) {
            const yy = y + rep * SIZE;
            if (yy < -amp - w || yy > SIZE + amp + w) continue;
            ctx.beginPath();
            for (let x = 0; x <= SIZE; x += step) {
                const py = yy + Math.sin((x / SIZE) * TAU * cyc + ph) * amp;
                if (x === 0) ctx.moveTo(x, py); else ctx.lineTo(x, py);
            }
            ctx.stroke();
        }
    }
    ctx.globalAlpha = 1;
}

// Short straight scratches, the handling a block of metal picks up. Kept inside drawWrapped so one that crosses an
// edge continues on the other side instead of stopping at the seam.
function scratches(ctx, seed, salt, count, lenLo, lenHi, angLo, angHi, light, dark, aLo, aHi) {
    ctx.lineCap = 'round';
    for (let s = 0; s < count; s++) {
        const x = hash01(s + seed, salt + 1) * SIZE, y = hash01(s + seed, salt + 2) * SIZE;
        const a = angLo + hash01(s + seed, salt + 3) * (angHi - angLo) + (hash01(s, seed + salt + 4) > 0.5 ? Math.PI : 0);
        const len = lenLo + hash01(s + seed, salt + 5) * (lenHi - lenLo);
        const dx = Math.cos(a) * len * 0.5, dy = Math.sin(a) * len * 0.5;
        ctx.globalAlpha = aLo + hash01(s, seed + salt + 6) * (aHi - aLo);
        ctx.strokeStyle = hash01(s, seed + salt + 7) > 0.5 ? light : dark;
        ctx.lineWidth = 0.8 + hash01(s, seed + salt + 8) * 1.1;
        drawWrapped(SIZE, x, y, len * 0.5 + 2, (px, py) => {
            ctx.beginPath(); ctx.moveTo(px - dx, py - dy); ctx.lineTo(px + dx, py + dy); ctx.stroke();
        });
    }
    ctx.globalAlpha = 1;
}

// A closed polygon from parallel vertex arrays, offset to (px, py)…
function polyPath(ctx, xs, ys, px, py) {
    linePath(ctx, xs, ys, px, py);
    ctx.closePath();
}
// …and the same points as an OPEN line, for a crack, which a closing segment would turn into a sliver of a triangle.
function linePath(ctx, xs, ys, px, py) {
    ctx.beginPath();
    for (let v = 0; v < xs.length; v++) {
        if (v === 0) ctx.moveTo(px + xs[v], py + ys[v]); else ctx.lineTo(px + xs[v], py + ys[v]);
    }
}


// ALUMINUM — rolled sheet stock with a brushed finish.
//
// Reference: a length of brushed aluminium bar or a rolled plate under a shop light. The grain is what carries it: a
// few hundred hairlines all running one way, so fine that no single one is seen, only the direction. Across them lies
// the one thing brushing does that nothing else does, an ANISOTROPIC sheen: the highlight smears out perpendicular to
// the grain into a soft band, so here the bands run vertically over horizontal grain. Around that, a faint milky oxide
// haze (aluminium's own film is colourless) and a few handling scratches across the grain, visible because they are.
// It is the brightest and cleanest canvas in this file, sitting at 224 with crests to white.
export function createAluminumTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Aluminum');
    ctx.fillStyle = grey(224);
    ctx.fillRect(0, 0, SIZE, SIZE);

    // The sheen, drawn as horizontal bands in a frame turned a quarter turn, so on the tile they stand vertical.
    // drawWrappedBand wraps in its own y, and the tile is square, so the turn keeps the wrap on both axes.
    ctx.save();
    ctx.translate(SIZE, 0);
    ctx.rotate(Math.PI / 2);
    for (let i = 0; i < 4; i++) {
        const dark = i === 3;
        drawWrappedBand(ctx, SIZE, hash01(i + seed, 1) * SIZE,
            8 + hash01(i + seed, 2) * 16, 3 + hash01(i + seed, 3) * 6, 1, hash01(i + seed, 4) * TAU,
            dark ? 120 + hash01(i + seed, 5) * 80 : 160 + hash01(i + seed, 5) * 150,
            dark ? grey(176) : '#FFFFFF', dark ? 0.07 : 0.13);
    }
    ctx.restore();

    hairlines(ctx, seed, 10, 620, '#FFFFFF', grey(150), 0.05, 0.16, 0.5, 1.5, 0.9, 64);   // the brushed grain
    hairlines(ctx, seed, 20, 14, '#FFFFFF', grey(172), 0.05, 0.10, 2.5, 6, 2.5, 64);      // roll marks, wider and fainter
    icePools(ctx, SIZE, seed, 30, 12, 60, 170, 255, 0.10, 196, 0.05);                      // the oxide haze
    scratches(ctx, seed, 40, 20, 30, 130, -1.2, -0.3, '#FFFFFF', grey(140), 0.10, 0.22);  // across the grain

    return asTexture(canvas);
}


// CALCIUM — a soft alkaline-earth metal, and it is never seen bright. Cut or cast, it is dull grey within the hour and
// then grows a WHITE crust of oxide and hydroxide that dusts off on the fingers. So this is the inverse of Potassium's
// look: there, bright metal islands survive in a dark crust; here, a dull metal carries blotches of chalk. The crust
// is matte, sits a hair proud (a soft shadow on its down-right side), sheds a powdery halo round every patch, and the
// larger patches craze as they dry.
export function createCalciumTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Calcium');
    ctx.fillStyle = grey(194);
    ctx.fillRect(0, 0, SIZE, SIZE);
    icePools(ctx, SIZE, seed, 0, 16, 80, 220, 226, 0.12, 132, 0.12);   // uneven tarnish on the metal itself

    // The crust, many small patches and a few large (a squared draw), so it reads as drifts rather than spots. Ragged
    // outlines (eleven lobes, half-radius jitter): a round patch reads as a bubble, and chalk never grows round.
    for (let c = 0; c < 120; c++) {
        const x = hash01(c + seed, 11) * SIZE, y = hash01(c + seed, 12) * SIZE;
        const r = 10 + hash01(c + seed, 13) * hash01(c + seed, 14) * 100;
        const a = 0.55 + hash01(c, seed + 15) * 0.35;
        drawWrapped(SIZE, x, y, r * 1.6, (px, py) => {
            ctx.fillStyle = greyA(255, a * 0.22);                                   // the powdery halo
            iceBlobPath(ctx, seed, c, px, py, r * 1.35, 0.5, 11, 0, 0); ctx.fill();
            ctx.fillStyle = greyA(118, 0.22);                                       // sitting proud: its shadow
            iceBlobPath(ctx, seed + 7, c, px + 1.5 + r * 0.06, py + 2 + r * 0.08, r, 0.48, 11, 0, 0); ctx.fill();
            ctx.fillStyle = greyA(250, a);                                          // the chalk
            iceBlobPath(ctx, seed + 7, c, px, py, r, 0.48, 11, 0, 0); ctx.fill();
        });
        if (r < 44) continue;
        // Crazing in the bigger patches: short jagged hairlines, each starting inside the patch it belongs to.
        ctx.lineWidth = 1;
        ctx.lineJoin = 'round';
        const cracks = 3 + Math.floor(hash01(c + seed, 16) * 4);
        for (let k = 0; k < cracks; k++) {
            const sx = (hash01(c * 13 + k + seed, 17) - 0.5) * r * 0.9, sy = (hash01(c * 13 + k + seed, 18) - 0.5) * r * 0.9;
            let ang = hash01(c * 13 + k + seed, 19) * TAU;
            const xs = [sx], ys = [sy];
            for (let s = 0; s < 4; s++) {
                ang += (hash01(c * 31 + k * 5 + s + seed, 20) - 0.5) * 1.6;
                const l = r * (0.10 + hash01(c * 31 + k * 5 + s + seed, 21) * 0.14);
                xs.push(xs[s] + Math.cos(ang) * l); ys.push(ys[s] + Math.sin(ang) * l);
            }
            ctx.globalAlpha = 0.30 + hash01(c * 7 + k, seed + 22) * 0.25;
            ctx.strokeStyle = grey(150);
            drawWrapped(SIZE, x, y, r * 1.5, (px, py) => {                 // a crack reaches at most ~1.4r from the centre
                ctx.beginPath();
                for (let s = 0; s < xs.length; s++) { if (s === 0) ctx.moveTo(px + xs[s], py + ys[s]); else ctx.lineTo(px + xs[s], py + ys[s]); }
                ctx.stroke();
            });
        }
        ctx.globalAlpha = 1;
    }

    iceGrainField(ctx, SIZE, seed, 50, 1400, [greyA(255, 0.45), greyA(160, 0.22), greyA(236, 0.35)], 2.0);   // loose powder
    for (let p = 0; p < 60; p++) {                                        // pits, each with its lit far wall
        const x = hash01(p + seed, 61) * SIZE, y = hash01(p + seed, 62) * SIZE;
        const r = 2 + hash01(p + seed, 63) * 5;
        ctx.globalAlpha = 0.25 + hash01(p, seed + 64) * 0.3;
        drawWrapped(SIZE, x, y, r * 2 + 3, (px, py) => {
            ctx.fillStyle = grey(96);
            ctx.beginPath(); ctx.arc(px, py, r, 0, TAU); ctx.fill();
            ctx.fillStyle = '#FFFFFF';
            ctx.beginPath(); ctx.arc(px + r * 0.5, py + r * 0.5, r * 0.62, 0, TAU); ctx.fill();
        });
    }
    ctx.globalAlpha = 1;
    return asTexture(canvas);
}


// SODIUM — a lump cut with a knife. Sodium is soft enough to slice like cold butter, and the cut face is the only time
// you see the metal: a flat, waxy, silver plane with the blade's drag lines across it, set into the grey-white crust
// of hydroxide and carbonate that covers everything else. The cuts are the recognition: straight edges and flat
// planes, each with its own drag direction. That is also what keeps it apart from Potassium, its neighbour in the
// table, which is drawn smeared and bloomed and never cut.
export function createSodiumTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Sodium');
    ctx.fillStyle = grey(204);
    ctx.fillRect(0, 0, SIZE, SIZE);
    icePools(ctx, SIZE, seed, 0, 18, 60, 190, 236, 0.18, 138, 0.16);
    iceGrainField(ctx, SIZE, seed, 10, 1700, [greyA(250, 0.45), greyA(120, 0.30), greyA(168, 0.30)], 2.6);
    for (let n = 0; n < 110; n++) {                                       // crumbs of crust, each lifted a little
        const x = hash01(n + seed, 21) * SIZE, y = hash01(n + seed, 22) * SIZE;
        const r = 3 + hash01(n + seed, 23) * 11;
        drawWrapped(SIZE, x, y, r * 1.6, (px, py) => {
            ctx.fillStyle = greyA(104, 0.32);
            iceBlobPath(ctx, seed, n, px + r * 0.3, py + r * 0.35, r, 0.4, 6, 0, 0); ctx.fill();
            ctx.fillStyle = greyA(232, 0.80);
            iceBlobPath(ctx, seed, n, px, py, r, 0.4, 6, 0, 0); ctx.fill();
        });
    }

    // THE CUTS. Four or five sides each, because a knife makes a few long straight edges and never a curve. A cut
    // face is a small flat MIRROR: it reflects the room, bright where it faces the light and dark where it does not,
    // so each takes its own steep gradient at its own angle. That, far more than being pale, is what makes it read as
    // metal rather than as a scrap of paper laid on the crust.
    for (let k = 0; k < 6; k++) {
        const cx = hash01(k + seed, 41) * SIZE, cy = hash01(k + seed, 42) * SIZE;
        const R = 100 + hash01(k + seed, 43) * 120;
        const nv = 4 + Math.floor(hash01(k + seed, 44) * 2);
        const a0 = hash01(k + seed, 45) * TAU;
        const xs = [], ys = [];
        for (let v = 0; v < nv; v++) {
            const ang = a0 + (v + (hash01(k * 11 + v + seed, 46) - 0.5) * 0.55) * TAU / nv;
            const rr = R * (0.66 + hash01(k * 13 + v + seed, 47) * 0.34);
            xs.push(Math.cos(ang) * rr); ys.push(Math.sin(ang) * rr);
        }
        const drag = hash01(k + seed, 48) * Math.PI;                     // the way the blade was drawn
        const cdx = Math.cos(drag), cdy = Math.sin(drag), nx = -cdy, ny = cdx;
        const pitch = 5 + hash01(k, seed + 49) * 4;
        const tilt = hash01(k + seed, 52) * TAU, tx = Math.cos(tilt) * R, ty = Math.sin(tilt) * R;
        const hi = 0.30 + hash01(k, seed + 53) * 0.25;                    // where across the face the reflection is brightest
        drawWrapped(SIZE, cx, cy, R + 14, (px, py) => {
            ctx.lineJoin = 'miter';
            ctx.lineWidth = 7; ctx.strokeStyle = greyA(96, 0.30);        // the crust's cut edge, standing proud of the face
            polyPath(ctx, xs, ys, px + 1.5, py + 2); ctx.stroke();
            const g = ctx.createLinearGradient(px - tx, py - ty, px + tx, py + ty);
            g.addColorStop(0, grey(226)); g.addColorStop(hi, grey(255)); g.addColorStop(Math.min(0.95, hi + 0.22), grey(196));
            g.addColorStop(1, grey(150));
            ctx.fillStyle = g; polyPath(ctx, xs, ys, px, py); ctx.fill();
            ctx.save();
            polyPath(ctx, xs, ys, px, py); ctx.clip();
            ctx.lineWidth = 1.3;
            let line = 0;
            for (let s = -R; s <= R; s += pitch, line++) {               // the drag lines, inside the cut only
                ctx.strokeStyle = hash01(k * 131 + line, seed + 50) > 0.5 ? '#FFFFFF' : grey(140);
                ctx.globalAlpha = 0.14 + hash01(k * 137 + line, seed + 51) * 0.18;
                const sx = px + nx * s, sy = py + ny * s;
                ctx.beginPath(); ctx.moveTo(sx - cdx * R * 1.2, sy - cdy * R * 1.2); ctx.lineTo(sx + cdx * R * 1.2, sy + cdy * R * 1.2); ctx.stroke();
            }
            ctx.globalAlpha = 1;
            ctx.lineWidth = 16; ctx.strokeStyle = greyA(200, 0.30);       // tarnish already creeping in from the edge
            polyPath(ctx, xs, ys, px, py); ctx.stroke();
            ctx.restore();
            ctx.lineWidth = 2; ctx.strokeStyle = greyA(110, 0.55);       // the lip of crust the blade cut through
            polyPath(ctx, xs, ys, px, py); ctx.stroke();
            ctx.lineWidth = 1.2; ctx.strokeStyle = greyA(255, 0.65);     // and the bright arris just inside it
            polyPath(ctx, xs, ys, px + 1.4, py + 1.4); ctx.stroke();
        });
    }
    return asTexture(canvas);
}


// MANGANESE — electrolytic manganese flake, which is how the metal is actually sold: hard, BRITTLE plates that have
// cracked apart as they were stripped from the cathode. So the surface is a mosaic of plates, each its own grey with a
// faint warm cast (manganese tarnishes toward a pinkish brown), split by jagged dark cracks, glittering with the
// granular fracture a brittle metal leaves, and marked with the near-black of its own oxide.
//
// The plates are a jittered lattice whose jitter is keyed on the cell index MODULO the lattice size, so the point at
// column n is the point at column 0 one tile over and the mosaic is genuinely periodic, not pinned flat at the edge.
export function createManganeseTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Manganese');
    const n = 9, cell = SIZE / n;
    const mod = (v) => ((v % n) + n) % n;
    const P = (i, j) => [i * cell + (hash01(mod(i) + seed, mod(j) * 4 + 1) - 0.5) * cell * 0.62,
                         j * cell + (hash01(mod(i) + seed, mod(j) * 4 + 2) - 0.5) * cell * 0.62];
    const quad = (a, b, c, d) => { ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.lineTo(c[0], c[1]); ctx.lineTo(d[0], d[1]); ctx.closePath(); };

    ctx.fillStyle = grey(150);
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let j = -1; j <= n; j++) for (let i = -1; i <= n; i++) {       // one row and column past each edge: the wrap
        const a = P(i, j), b = P(i + 1, j), c = P(i + 1, j + 1), d = P(i, j + 1);
        const v = 152 + hash01(mod(i) + seed, mod(j) + 71) * 72;
        ctx.fillStyle = 'rgb(' + Math.round(v + 5) + ',' + Math.round(v) + ',' + Math.round(v - 4) + ')';
        quad(a, b, c, d); ctx.fill();
        const g = ctx.createLinearGradient(a[0], a[1], c[0], c[1]);      // each plate tilts a little: lit up-left
        const tilt = 0.10 + hash01(mod(i) + seed, mod(j) + 72) * 0.16;
        g.addColorStop(0, greyA(255, tilt)); g.addColorStop(0.5, greyA(255, 0)); g.addColorStop(1, greyA(40, tilt));
        ctx.fillStyle = g; quad(a, b, c, d); ctx.fill();
    }

    for (let o = 0; o < 18; o++) {                                        // the black oxide, before the cracks cut it
        const x = hash01(o + seed, 81) * SIZE, y = hash01(o + seed, 82) * SIZE;
        const r = 18 + hash01(o + seed, 83) * 60;
        const a = 0.25 + hash01(o, seed + 84) * 0.28;
        drawWrapped(SIZE, x, y, r, (px, py) => {
            const g = ctx.createRadialGradient(px, py, 0, px, py, r);
            g.addColorStop(0, rgbA(62, 52, 48, a)); g.addColorStop(0.6, rgbA(70, 60, 56, a * 0.5)); g.addColorStop(1, rgbA(80, 70, 66, 0));
            ctx.fillStyle = g; ctx.beginPath(); ctx.arc(px, py, r, 0, TAU); ctx.fill();
        });
    }

    iceGrainField(ctx, SIZE, seed, 90, 1700, [greyA(255, 0.55), greyA(92, 0.34)], 1.6);   // granular fracture glitter

    // THE CRACKS: every lattice edge once, as a jagged line. Its jaggedness and width are keyed on the edge's own
    // (modular) index, so an edge drawn from either tile is the same crack. The dark line is the crack; the faint
    // bright one beside it is its far wall catching the light from the upper left.
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    const crack = (p0, p1, key) => {
        const dx = p1[0] - p0[0], dy = p1[1] - p0[1], len = Math.hypot(dx, dy) || 1;
        const nx = -dy / len, ny = dx / len;
        const xs = [p0[0]], ys = [p0[1]];
        for (let t = 1; t < 5; t++) {
            const off = (hash01(key * 5 + t + seed, 91) - 0.5) * 14;
            xs.push(p0[0] + dx * t / 5 + nx * off); ys.push(p0[1] + dy * t / 5 + ny * off);
        }
        xs.push(p1[0]); ys.push(p1[1]);
        const w = 1.2 + hash01(key + seed, 92) * hash01(key + seed, 93) * 4.2;
        const line = (ox, oy) => { ctx.beginPath(); for (let t = 0; t < xs.length; t++) { if (t === 0) ctx.moveTo(xs[t] + ox, ys[t] + oy); else ctx.lineTo(xs[t] + ox, ys[t] + oy); } ctx.stroke(); };
        ctx.globalAlpha = 0.85; ctx.strokeStyle = grey(52); ctx.lineWidth = w; line(0, 0);
        ctx.globalAlpha = 0.28; ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 1; line(w * 0.5 + 0.8, w * 0.5 + 0.8);
    };
    for (let j = -1; j <= n; j++) for (let i = -1; i <= n; i++) {
        const k = mod(i) * n + mod(j);
        crack(P(i, j), P(i + 1, j), k * 2);
        crack(P(i, j), P(i, j + 1), k * 2 + 1);
    }
    ctx.globalAlpha = 1;
    return asTexture(canvas);
}


// TITANIUM — bead-blasted, and heat-tinted. Blasted titanium is an even satin grey; what nobody mistakes for any other
// metal is the colour it takes when it is heated, because its oxide film grows in thickness bands that interfere with
// light: straw, then bronze, purple, deep blue and a pale sky blue, in that order, as the heat rises.
//
// Each colour is laid down as its OWN broad, soft wash, and the washes overlap, so the tint drifts from one colour into
// the next across the plate the way it does on a pipe that has been welded. The first version drew each wash as a
// radial gradient through the whole sequence, and concentric rings that tight read as eyes: a wall of titanium became
// a wall of staring rings. The washes sit on a jittered three-by-two so every quarter of the tile (all an icon or a bit
// shows) carries some colour. They are multiplied by the pale grey hex on the GPU and survive it, which is the one
// reason this generator is allowed to carry colour at all. Under them, a blast stipple coarse enough to read as satin.
const TITANIUM_TINT = [[222, 200, 138], [206, 158, 96], [140, 96, 168], [80, 116, 198], [150, 190, 228], [190, 150, 110]];
export function createTitaniumTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Titanium');
    ctx.fillStyle = grey(212);
    ctx.fillRect(0, 0, SIZE, SIZE);
    icePools(ctx, SIZE, seed, 0, 12, 100, 260, 238, 0.10, 166, 0.09);
    // The blast: pits of three or four pixels, which is what survives being shipped at 512 and drawn a kube face across.
    // Finer than that and the satin averaged out to a blur before it ever reached the screen.
    iceGrainField(ctx, SIZE, seed, 20, 2400, [greyA(252, 0.46), greyA(142, 0.42), greyA(222, 0.34)], 3.4);

    for (let k = 0; k < TITANIUM_TINT.length; k++) {
        const [r, g, b] = TITANIUM_TINT[k];
        const x = ((k % 3) + 0.2 + hash01(k + seed, 31) * 0.6) * SIZE / 3, y = (Math.floor(k / 3) + 0.2 + hash01(k + seed, 32) * 0.6) * SIZE / 2;
        const R = 260 + hash01(k + seed, 33) * 150;
        const squash = 0.55 + hash01(k + seed, 34) * 0.40, ang = hash01(k + seed, 35) * Math.PI;
        const a = 0.24 + hash01(k, seed + 36) * 0.12;
        drawWrapped(SIZE, x, y, R, (px, py) => {
            ctx.save();
            ctx.translate(px, py); ctx.rotate(ang); ctx.scale(1, squash);
            const wash = ctx.createRadialGradient(0, 0, 0, 0, 0, R);
            wash.addColorStop(0, rgbA(r, g, b, a)); wash.addColorStop(0.55, rgbA(r, g, b, a * 0.6)); wash.addColorStop(1, rgbA(r, g, b, 0));
            ctx.fillStyle = wash;
            ctx.beginPath(); ctx.arc(0, 0, R, 0, TAU); ctx.fill();
            ctx.restore();
        });
    }
    scratches(ctx, seed, 50, 14, 20, 90, -0.9, 0.9, '#FFFFFF', grey(150), 0.08, 0.18);
    return asTexture(canvas);
}


// CHROMIUM — a mirror. Polished chrome has no colour and no texture of its own; everything you see in it is the world
// it reflects, and the one thing every reflection in it shares is a HORIZON: bright sky above, a hard dark edge, the
// darker ground below fading back up. So that is what is painted: two horizons per tile (so any quarter of the tile,
// which is what an icon or a bit shows, carries one), each a whole-cycle wave so it runs off one edge and onto the
// other, with a bright rim just above the edge, crisp highlight streaks in the sky, and nothing else. Its contrast is
// the widest in this file, and that contrast is the chrome.
export function createChromiumTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Chromium');
    const HALF = SIZE / 2, OVER = 48;
    const horizon = (rep, x) => {                                        // the wavy edge, whole cycles so it wraps
        const s = rep * 17;
        return rep * HALF + HALF * 0.5
            + Math.sin((x / SIZE) * TAU * (1 + Math.floor(hash01(s + seed, 1) * 2)) + hash01(s + seed, 2) * TAU) * (16 + hash01(s + seed, 3) * 14)
            + Math.sin((x / SIZE) * TAU * (3 + Math.floor(hash01(s + seed, 4) * 3)) + hash01(s + seed, 5) * TAU) * (4 + hash01(s + seed, 6) * 6);
    };
    const trace = (rep, dy) => {
        for (let x = -OVER; x <= SIZE + OVER; x += 16) {
            if (x === -OVER) ctx.moveTo(x, horizon(rep, x) + dy); else ctx.lineTo(x, horizon(rep, x) + dy);
        }
    };
    for (let rep = 0; rep < 2; rep++) {
        const y0 = rep * HALF;
        const sky = ctx.createLinearGradient(0, y0, 0, y0 + HALF);       // each half ends where the next begins: it tiles
        sky.addColorStop(0.00, grey(196)); sky.addColorStop(0.30, grey(228)); sky.addColorStop(0.50, grey(255)); sky.addColorStop(1.00, grey(196));
        ctx.fillStyle = sky;
        ctx.fillRect(0, y0, SIZE, HALF);
    }
    for (let rep = 0; rep < 2; rep++) {
        const y0 = rep * HALF;
        const ground = ctx.createLinearGradient(0, y0 + HALF * 0.44, 0, y0 + HALF);
        ground.addColorStop(0.00, grey(58)); ground.addColorStop(0.18, grey(64)); ground.addColorStop(0.62, grey(126)); ground.addColorStop(1.00, grey(196));
        ctx.fillStyle = ground;
        ctx.beginPath();
        trace(rep, 0);
        ctx.lineTo(SIZE + OVER, y0 + HALF); ctx.lineTo(-OVER, y0 + HALF);
        ctx.closePath();
        ctx.fill();
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#FFFFFF';                                       // the rim of light on the horizon
        ctx.globalAlpha = 0.22; ctx.lineWidth = 12; ctx.beginPath(); trace(rep, -8); ctx.stroke();
        ctx.globalAlpha = 0.90; ctx.lineWidth = 3;  ctx.beginPath(); trace(rep, -2.5); ctx.stroke();
        ctx.strokeStyle = grey(30);                                        // and the hard dark edge under it
        ctx.globalAlpha = 0.55; ctx.lineWidth = 3;  ctx.beginPath(); trace(rep, 2); ctx.stroke();
        ctx.globalAlpha = 1;
    }
    // Highlight streaks: reflections of long lights, in the sky half of each band, bright and thin.
    for (let i = 0; i < 8; i++) {
        const rep = i % 2;
        drawWrappedBand(ctx, SIZE, rep * HALF + HALF * (0.10 + hash01(i + seed, 21) * 0.26),
            3 + hash01(i + seed, 22) * 8, 1 + hash01(i + seed, 23) * 3, 1 + Math.floor(hash01(i + seed, 24) * 2),
            hash01(i + seed, 25) * TAU, 1.5 + hash01(i + seed, 26) * 4, '#FFFFFF', 0.45 + hash01(i, seed + 27) * 0.35);
    }
    // …and fainter ones in the ground, so it reads as a reflected landscape rather than as a painted band.
    for (let i = 0; i < 6; i++) {
        const rep = i % 2;
        drawWrappedBand(ctx, SIZE, rep * HALF + HALF * (0.66 + hash01(i + seed, 31) * 0.22),
            4 + hash01(i + seed, 32) * 10, 2 + hash01(i + seed, 33) * 3, 1 + Math.floor(hash01(i + seed, 34) * 2),
            hash01(i + seed, 35) * TAU, 3 + hash01(i + seed, 36) * 8, grey(170), 0.20 + hash01(i, seed + 37) * 0.15);
    }
    return asTexture(canvas);
}


// MAGNESIUM — the top of a cast ingot. Magnesium freezes in DENDRITES: fern-like crystals with a straight spine and
// side branches at sixty degrees (its crystal is hexagonal), and they stay on the ingot's face, printed in relief, under
// a dull whitish oxide film. So each dendrite here is a spine at a multiple of sixty degrees, alternating branches off
// it that shorten toward the tip, and twigs off those. A few nucleated as six-armed stars. Every line is drawn twice,
// a soft shadow down-right and a light crest up-left, which is what makes them stand up off the metal rather than
// look scratched into it.
export function createMagnesiumTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Magnesium');
    ctx.fillStyle = grey(198);
    ctx.fillRect(0, 0, SIZE, SIZE);
    icePools(ctx, SIZE, seed, 0, 14, 90, 240, 242, 0.14, 150, 0.10);   // the oxide film, never even

    const STEP = 16;
    const arm = (d, a, L, out) => {                                      // one spine with its branches and twigs
        const sx = [], sy = [];                                          // the spine, drifting slightly as it grows
        let x = 0, y = 0, ang = a;
        const curl = (hash01(d + seed, 5) - 0.5) * 0.35;
        for (let s = 0; s <= L; s += STEP) {
            sx.push(x); sy.push(y);
            ang += curl * STEP / L;
            x += Math.cos(ang) * STEP; y += Math.sin(ang) * STEP;
        }
        out[0].push([sx, sy]);
        for (let k = 1; k < sx.length - 1; k++) {
            const side = k % 2 ? 1 : -1;
            const t = k / (sx.length - 1);
            const ba = a + side * Math.PI / 3 + (hash01(d * 29 + k + seed, 6) - 0.5) * 0.12;
            const bl = L * (1 - t) * (0.34 + hash01(d * 31 + k + seed, 7) * 0.16);
            if (bl < 6) continue;
            const bx = [sx[k], sx[k] + Math.cos(ba) * bl], by = [sy[k], sy[k] + Math.sin(ba) * bl];
            out[1].push([bx, by]);
            for (let q = 11; q < bl - 5; q += 11) {                       // twigs, alternating, sixty degrees off the branch
                const tside = (q / 11) % 2 ? 1 : -1;
                const ta = ba + tside * Math.PI / 3;
                const tl = (bl - q) * 0.36;
                const ox = sx[k] + Math.cos(ba) * q, oy = sy[k] + Math.sin(ba) * q;
                out[2].push([[ox, ox + Math.cos(ta) * tl], [oy, oy + Math.sin(ta) * tl]]);
            }
        }
    };
    const strokeAll = (lines, px, py) => {
        ctx.beginPath();
        for (const [xs, ys] of lines) {
            for (let v = 0; v < xs.length; v++) { if (v === 0) ctx.moveTo(px + xs[v], py + ys[v]); else ctx.lineTo(px + xs[v], py + ys[v]); }
        }
        ctx.stroke();
    };
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const WIDTH = [5.2, 3.2, 1.9];
    for (let d = 0; d < 15; d++) {
        const x0 = hash01(d + seed, 1) * SIZE, y0 = hash01(d + seed, 2) * SIZE;
        const star = hash01(d + seed, 3) < 0.25;
        const a0 = Math.floor(hash01(d + seed, 4) * 6) * Math.PI / 3 + (hash01(d, seed + 8) - 0.5) * 0.2;
        const L = star ? 90 + hash01(d, seed + 9) * 70 : 170 + hash01(d, seed + 9) * 200;
        const lines = [[], [], []];
        for (let k = 0; k < (star ? 6 : 1); k++) arm(d * 7 + k, a0 + k * Math.PI / 3, L * (star ? 0.8 + hash01(d * 7 + k, seed + 10) * 0.4 : 1), lines);
        drawWrapped(SIZE, x0, y0, L * 1.25 + 8, (px, py) => {
            for (let lv = 0; lv < 3; lv++) {
                ctx.globalAlpha = 0.42; ctx.strokeStyle = grey(104); ctx.lineWidth = WIDTH[lv] + 1.4;
                strokeAll(lines[lv], px + 2, py + 2);
                ctx.globalAlpha = 0.80; ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = WIDTH[lv];
                strokeAll(lines[lv], px - 0.8, py - 0.8);
            }
        });
    }
    ctx.globalAlpha = 1;
    iceGrainField(ctx, SIZE, seed, 40, 900, [greyA(255, 0.35), greyA(150, 0.22)], 1.8);
    return asTexture(canvas);
}


// COBALT — electrodeposited nodules. Cobalt is refined by electrowinning, and what comes off the cathode is a crust of
// rounded nodules grown on nodules, lustrous and faintly blue (the blue is the hex's, #8080A0). Packed like grapes,
// big ones first and small ones sitting on them, each lit from the upper left with a crisp specular point, over the
// dark crevices between them.
//
// A thousand nodules with a radial gradient each would be a thousand gradients. They are drawn instead from three
// small SPRITES, each a nodule rendered once with its contact shadow and highlight, scaled per nodule with drawImage:
// one gradient per sprite, and a nodule costs one image draw, which is the cheapest thing a canvas does.
export function createCobaltTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Cobalt');
    ctx.fillStyle = grey(108);
    ctx.fillRect(0, 0, SIZE, SIZE);
    icePools(ctx, SIZE, seed, 0, 10, 80, 200, 150, 0.12, 70, 0.14);

    const SP = 128, SC = 54, SR = 46;                                   // sprite size, nodule centre and radius in it
    const sprites = [0, 1, 2].map((v) => {
        const c = document.createElement('canvas');
        c.width = c.height = SP;
        const s = c.getContext('2d');
        s.fillStyle = greyA(24, 0.45);                                   // contact shadow, down-right
        s.beginPath(); s.arc(SC + SR * 0.22, SC + SR * 0.28, SR * 1.04, 0, TAU); s.fill();
        const hx = SC - SR * (0.30 + v * 0.05), hy = SC - SR * (0.36 + v * 0.04);
        const g = s.createRadialGradient(hx, hy, SR * 0.04, SC, SC, SR * 1.02);
        g.addColorStop(0, grey(255)); g.addColorStop(0.24, grey(222 - v * 8)); g.addColorStop(0.64, grey(164 - v * 10)); g.addColorStop(1, grey(104 - v * 8));
        s.fillStyle = g;
        s.beginPath(); s.arc(SC, SC, SR, 0, TAU); s.fill();
        s.fillStyle = greyA(255, 0.88);                                  // the specular point
        s.beginPath(); s.arc(hx - SR * 0.04, hy - SR * 0.04, SR * 0.15, 0, TAU); s.fill();
        return c;
    });

    const N = 950, nods = [];
    for (let i = 0; i < N; i++) {
        nods.push({ i, x: hash01(i + seed, 1) * SIZE, y: hash01(i + seed, 2) * SIZE, r: 8 + Math.pow(hash01(i + seed, 3), 2.2) * 40 });
    }
    nods.sort((a, b) => (b.r - a.r) || (a.i - b.i));                     // big first; the index breaks ties, so the order is the seed's
    for (const nd of nods) {
        const sc = nd.r / SR, sp = sprites[Math.floor(hash01(nd.i, seed + 4) * 3)];
        drawWrapped(SIZE, nd.x, nd.y, nd.r * 1.4 + 2, (px, py) => {
            ctx.drawImage(sp, px - SC * sc, py - SC * sc, SP * sc, SP * sc);
        });
    }
    return asTexture(canvas);
}


// ZIRCONIUM — a polished crystal bar. Zirconium refined by the crystal-bar process comes out as a rod of large metal
// crystals, and a polished and etched face shows them as GRAINS: a patchwork in which each crystal, lying at its own
// angle to the light, flashes its own brightness, some with the fine parallel striations of twinning, all divided by
// crisp thin boundaries. Clean and bright where Manganese is cracked and dark.
//
// The grains are the dual of a jittered triangular lattice: each lattice point's grain is the polygon through the
// centroids of the six triangles around it. A centroid is shared by exactly the three grains that meet there, so the
// grains close up with no gaps and no overlaps, and the lattice (7 columns, 8 rows, jitter keyed modulo both) repeats
// exactly at the tile edge. Eight rows, not seven: the odd rows are offset half a column, so the row count has to be
// even for row 8 to be row 0 again.
export function createZirconiumTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Zirconium');
    const m = 7, k = 8, pitch = SIZE / m, rowH = SIZE / k;
    const mi = (v) => ((v % m) + m) % m, mj = (v) => ((v % k) + k) % k;
    // Jitter of half a cell either way: enough that no two grains are the same shape and the lattice stops reading as
    // honeycomb, not so much that a point can cross the line between two of its neighbours and turn a triangle over.
    const P = (i, j) => [(i + (mj(j) % 2) * 0.5) * pitch + (hash01(mi(i) + seed, mj(j) * 3 + 1) - 0.5) * pitch * 0.5,
                         j * rowH + (hash01(mi(i) + seed, mj(j) * 3 + 2) - 0.5) * rowH * 0.5];
    // The six neighbours in order round the compass (E, SE, SW, W, NW, NE); which index is SE depends on whether the
    // row is shifted.
    const ring = (i, j) => (mj(j) % 2)
        ? [[i + 1, j], [i + 1, j + 1], [i, j + 1], [i - 1, j], [i, j - 1], [i + 1, j - 1]]
        : [[i + 1, j], [i, j + 1], [i - 1, j + 1], [i - 1, j], [i - 1, j - 1], [i, j - 1]];
    const grain = (i, j) => {
        const p = P(i, j), nb = ring(i, j).map(([a, b]) => P(a, b));
        const xs = [], ys = [];
        for (let q = 0; q < 6; q++) {
            const a = nb[q], b = nb[(q + 1) % 6];
            xs.push((p[0] + a[0] + b[0]) / 3); ys.push((p[1] + a[1] + b[1]) / 3);
        }
        return [xs, ys];
    };
    ctx.fillStyle = grey(180);
    ctx.fillRect(0, 0, SIZE, SIZE);
    const grains = [];
    for (let j = -1; j <= k; j++) for (let i = -1; i <= m; i++) grains.push([i, j, grain(i, j)]);
    for (const [i, j, [xs, ys]] of grains) {
        const h = hash01(mi(i) + seed, mj(j) + 41);
        const v = 128 + h * 127;                                        // each crystal at its own angle to the light
        ctx.fillStyle = grey(v);
        polyPath(ctx, xs, ys, 0, 0); ctx.fill();
        if (hash01(mi(i) + seed, mj(j) + 42) < 0.45) continue;          // most crystals twinned, not all
        ctx.save();
        polyPath(ctx, xs, ys, 0, 0); ctx.clip();
        const a = hash01(mi(i) + seed, mj(j) + 43) * Math.PI, ca = Math.cos(a), sa = Math.sin(a);
        const gap = 6 + hash01(mi(i) + seed, mj(j) + 44) * 5;
        const cx = (xs[0] + xs[3]) / 2, cy = (ys[0] + ys[3]) / 2;
        ctx.lineWidth = 1.4;
        let line = 0;
        for (let s = -110; s <= 110; s += gap, line++) {
            ctx.strokeStyle = line % 2 ? '#FFFFFF' : grey(90);
            ctx.globalAlpha = 0.09 + hash01(mi(i) * 64 + mj(j) * 8 + line, seed + 45) * 0.08;
            ctx.beginPath();
            ctx.moveTo(cx - sa * s - ca * 120, cy + ca * s - sa * 120);
            ctx.lineTo(cx - sa * s + ca * 120, cy + ca * s + sa * 120);
            ctx.stroke();
        }
        ctx.globalAlpha = 1;
        ctx.restore();
    }
    icePools(ctx, SIZE, seed, 50, 8, 140, 300, 255, 0.10, 120, 0.06);  // the polish, broad and soft over the lot
    ctx.lineJoin = 'round';
    for (const [, , [xs, ys]] of grains) {                              // the boundaries: an etched groove, lit on its far side
        ctx.globalAlpha = 0.70; ctx.strokeStyle = grey(74); ctx.lineWidth = 1.8;
        polyPath(ctx, xs, ys, 0, 0); ctx.stroke();
        ctx.globalAlpha = 0.30; ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 1;
        polyPath(ctx, xs, ys, 1.2, 1.2); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    return asTexture(canvas);
}


// STEEL — hot-rolled plate as it leaves the mill, then worked. Rolling at heat leaves MILL SCALE, a blue-black skin of
// iron oxide that cracks and flakes; before anything is welded to it the scale is ground back to bright metal with an
// angle grinder, which leaves its own unmistakable mark: fields of fine concentric arcs, swirling round a centre just
// off the ground patch. Dark flaky scale with bright swirled windows is the look of every piece of structural steel in
// a fabrication shop, and nothing else in the game has either half of it. The scale's blue is the hex's own
// (#71797E); the canvas only makes it dark.
export function createSteelTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Steel');
    ctx.fillStyle = grey(116);
    ctx.fillRect(0, 0, SIZE, SIZE);
    icePools(ctx, SIZE, seed, 0, 22, 50, 170, 164, 0.16, 70, 0.18);    // scale is never even

    for (let f = 0; f < 150; f++) {                                     // flakes of scale, lifted at the edge
        const x = hash01(f + seed, 11) * SIZE, y = hash01(f + seed, 12) * SIZE;
        const r = 6 + hash01(f + seed, 13) * 22;
        const n = 4 + Math.floor(hash01(f + seed, 14) * 3), a0 = hash01(f + seed, 15) * TAU;   // scale breaks in shards, not discs
        const xs = [], ys = [];
        for (let v = 0; v < n; v++) {
            const ang = a0 + (v + (hash01(f * 7 + v + seed, 16) - 0.5) * 0.6) * TAU / n, rr = r * (0.55 + hash01(f * 9 + v + seed, 17) * 0.45);
            xs.push(Math.cos(ang) * rr); ys.push(Math.sin(ang) * rr);
        }
        drawWrapped(SIZE, x, y, r * 1.5, (px, py) => {
            ctx.fillStyle = greyA(60, 0.35);
            polyPath(ctx, xs, ys, px + r * 0.18 + 1, py + r * 0.22 + 1); ctx.fill();
            ctx.fillStyle = greyA(142, 0.55);
            polyPath(ctx, xs, ys, px, py); ctx.fill();
            ctx.strokeStyle = greyA(214, 0.30); ctx.lineWidth = 1.2;
            polyPath(ctx, xs, ys, px - 0.8, py - 0.8); ctx.stroke();
        });
    }
    ctx.lineJoin = 'round';
    for (let c = 0; c < 220; c++) {                                     // the scale's crazing
        const x = hash01(c + seed, 21) * SIZE, y = hash01(c + seed, 22) * SIZE;
        let ang = hash01(c + seed, 23) * TAU;
        const xs = [0], ys = [0];
        for (let s = 0; s < 3; s++) {
            ang += (hash01(c * 7 + s + seed, 24) - 0.5) * 1.4;
            const l = 5 + hash01(c * 7 + s + seed, 25) * 12;
            xs.push(xs[s] + Math.cos(ang) * l); ys.push(ys[s] + Math.sin(ang) * l);
        }
        ctx.globalAlpha = 0.22 + hash01(c, seed + 26) * 0.2;
        ctx.strokeStyle = grey(170); ctx.lineWidth = 1;
        drawWrapped(SIZE, x, y, 50, (px, py) => { linePath(ctx, xs, ys, px, py); ctx.stroke(); });
    }
    ctx.globalAlpha = 1;

    // GROUND PATCHES. Each is a ragged blob clipped out of the scale, filled bright, and swirled by two passes of a
    // grinder whose centres sit off the patch, so only a sweep of each circle crosses it.
    for (let p = 0; p < 4; p++) {
        const x = hash01(p + seed, 31) * SIZE, y = hash01(p + seed, 32) * SIZE;
        const R = 84 + hash01(p + seed, 33) * 96;
        const passes = [0, 1].map((q) => {
            const phi = hash01(p * 3 + q + seed, 34) * TAU, dist = R * (1.25 + hash01(p * 3 + q + seed, 35) * 0.6);
            return { dx: Math.cos(phi) * dist, dy: Math.sin(phi) * dist, dist };
        });
        drawWrapped(SIZE, x, y, R * 1.3, (px, py) => {
            ctx.save();
            iceBlobPath(ctx, seed + 3, p, px, py, R, 0.26, 10, 0, 0); ctx.clip();
            ctx.fillStyle = grey(222);
            ctx.fillRect(px - R * 1.4, py - R * 1.4, R * 2.8, R * 2.8);
            let arc = 0;
            for (const { dx, dy, dist } of passes) {
                const gx = px + dx, gy = py + dy;
                const mid = Math.atan2(-dy, -dx), half = Math.asin(Math.min(1, (R * 1.35) / dist));
                for (let rr = dist - R * 1.35; rr < dist + R * 1.35; rr += 2.4 + hash01(p * 977 + arc, seed + 36) * 2.8, arc++) {
                    if (rr <= 1) continue;
                    ctx.strokeStyle = hash01(p * 977 + arc, seed + 37) > 0.45 ? '#FFFFFF' : grey(128);
                    ctx.globalAlpha = 0.16 + hash01(p * 983 + arc, seed + 38) * 0.30;
                    ctx.lineWidth = 0.8 + hash01(p * 991 + arc, seed + 39) * 1.6;
                    ctx.beginPath(); ctx.arc(gx, gy, rr, mid - half, mid + half); ctx.stroke();
                }
            }
            ctx.globalAlpha = 1;
            ctx.restore();
            ctx.lineWidth = 5; ctx.strokeStyle = greyA(82, 0.45);          // where the scale breaks off: a dark lip
            iceBlobPath(ctx, seed + 3, p, px, py, R, 0.26, 10, 0, 0); ctx.stroke();
            ctx.lineWidth = 1.6; ctx.strokeStyle = greyA(236, 0.45);       // and the bright feathered edge of the metal
            iceBlobPath(ctx, seed + 3, p, px + 1.4, py + 1.4, R * 0.985, 0.26, 10, 0, 0); ctx.stroke();
        });
    }
    return asTexture(canvas);
}


// STRUCTURAL ALLOY — a machined isogrid panel. Structural plate in aerospace is not left solid: it is milled into a
// grid of triangular pockets that leaves a lattice of ribs, stiff for its weight, with a round boss wherever ribs meet.
// It is the only surface here that reads as DESIGNED rather than formed, which is exactly what separates an engineered
// alloy from the raw metals around it. Each pocket has a floor carrying the concentric passes of the cutter and three
// walls that take the light by which way they face: in a recess the wall on the upper left is in shadow and the wall
// on the lower right catches the light.
//
// Triangles cannot be exactly equilateral and still tile a square, so the lattice is five columns by six rows, whose
// triangles have base angles of 59 degrees. Six rows, not five, because alternate rows are offset half a column and
// row 6 has to be row 0 again.
export function createStructuralAlloyTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately unread
    const { canvas, ctx } = surface();
    const seed = nameSeed('Structural Alloy');
    const m = 5, k = 6, pitch = SIZE / m, rowH = SIZE / k;
    const odd = (j) => (((j % 2) + 2) % 2) === 1;
    const node = (i, j) => [(i + (odd(j) ? 0.5 : 0)) * pitch, j * rowH];
    const RIB = 11, WALL = 7;
    const LX = -Math.SQRT1_2, LY = -Math.SQRT1_2;                        // toward the light, up and to the left

    ctx.fillStyle = grey(216);                                           // the rib tops: the plate's original face
    ctx.fillRect(0, 0, SIZE, SIZE);
    hairlines(ctx, seed, 10, 160, '#FFFFFF', grey(170), 0.05, 0.12, 0.6, 1.4, 0.8, 64);   // the plate's mill finish, left on the ribs

    const inset = (tri, d) => {                                          // a triangle shrunk by d from each edge, about its incentre
        const [A, B, C] = tri;
        const a = Math.hypot(B[0] - C[0], B[1] - C[1]), b = Math.hypot(A[0] - C[0], A[1] - C[1]), c = Math.hypot(A[0] - B[0], A[1] - B[1]);
        const per = a + b + c;
        const ix = (a * A[0] + b * B[0] + c * C[0]) / per, iy = (a * A[1] + b * B[1] + c * C[1]) / per;
        const area = Math.abs((B[0] - A[0]) * (C[1] - A[1]) - (C[0] - A[0]) * (B[1] - A[1])) / 2;
        const f = Math.max(0, (2 * area / per - d) / (2 * area / per));
        return tri.map(([x, y]) => [ix + (x - ix) * f, iy + (y - iy) * f]);
    };
    const tri = (T) => { ctx.beginPath(); ctx.moveTo(T[0][0], T[0][1]); ctx.lineTo(T[1][0], T[1][1]); ctx.lineTo(T[2][0], T[2][1]); ctx.closePath(); };
    const pocket = (T) => {
        const outer = inset(T, RIB), inner = inset(T, RIB + WALL);
        const cx = (T[0][0] + T[1][0] + T[2][0]) / 3, cy = (T[0][1] + T[1][1] + T[2][1]) / 3;
        for (let e = 0; e < 3; e++) {                                    // the walls, each shaded by the way it faces
            const o0 = outer[e], o1 = outer[(e + 1) % 3], i0 = inner[e], i1 = inner[(e + 1) % 3];
            const mx = (o0[0] + o1[0]) / 2 - cx, my = (o0[1] + o1[1]) / 2 - cy, ml = Math.hypot(mx, my) || 1;
            const facing = -(mx / ml) * LX - (my / ml) * LY;             // the wall faces back into the pocket, along -n
            ctx.fillStyle = grey(168 + facing * 62);
            ctx.beginPath(); ctx.moveTo(o0[0], o0[1]); ctx.lineTo(o1[0], o1[1]); ctx.lineTo(i1[0], i1[1]); ctx.lineTo(i0[0], i0[1]); ctx.closePath(); ctx.fill();
        }
        ctx.fillStyle = grey(170);                                       // the floor
        tri(inner); ctx.fill();
        ctx.strokeStyle = grey(146); ctx.lineWidth = 1.1;               // the cutter's concentric passes on it
        for (let pass = 1; pass <= 4; pass++) {
            ctx.globalAlpha = 0.22 - pass * 0.03;
            tri(inset(T, RIB + WALL + pass * 11)); ctx.stroke();
        }
        ctx.globalAlpha = 1;
        ctx.strokeStyle = greyA(250, 0.55); ctx.lineWidth = 1.4;        // the sharp top edge of the ribs round it
        tri(outer); ctx.stroke();
    };
    for (let j = -1; j < k + 1; j++) {                                   // one row past each edge, and the canvas clips the rest
        for (let i = -2; i <= m + 1; i++) {
            const a0 = node(i, j), a1 = node(i + 1, j), b0 = node(i, j + 1), b1 = node(i + 1, j + 1);
            if (!odd(j)) { pocket([a0, a1, b0]); pocket([b0, b1, a1]); }
            else { pocket([a0, a1, b1]); pocket([b0, b1, a0]); }
        }
    }
    for (let j = -1; j <= k + 1; j++) for (let i = -1; i <= m + 1; i++) {   // a boss wherever the ribs meet, with its bolt
        const [x, y] = node(i, j);
        const g = ctx.createRadialGradient(x - 7, y - 8, 2, x, y, 22);
        g.addColorStop(0, grey(250)); g.addColorStop(0.6, grey(222)); g.addColorStop(1, grey(170));
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, 21, 0, TAU); ctx.fill();
        ctx.strokeStyle = greyA(120, 0.55); ctx.lineWidth = 1.4; ctx.beginPath(); ctx.arc(x, y, 21, 0, TAU); ctx.stroke();
        ctx.fillStyle = grey(58); ctx.beginPath(); ctx.arc(x, y, 6, 0, TAU); ctx.fill();
        ctx.strokeStyle = greyA(255, 0.5); ctx.lineWidth = 1.2; ctx.beginPath(); ctx.arc(x, y, 6, Math.PI * 1.75, Math.PI * 0.75); ctx.stroke();
    }
    scratches(ctx, seed, 70, 16, 20, 80, -0.8, 0.8, '#FFFFFF', grey(120), 0.06, 0.16);
    return asTexture(canvas);
}

// The generator for each metal slot, keyed by the slot name texture-dispatch.js gives it.
export const METAL_SURFACES = new Map([
    ['aluminum',        createAluminumTexture],
    ['calcium',         createCalciumTexture],
    ['sodium',          createSodiumTexture],
    ['manganese',       createManganeseTexture],
    ['titanium',        createTitaniumTexture],
    ['chromium',        createChromiumTexture],
    ['magnesium',       createMagnesiumTexture],
    ['cobalt',          createCobaltTexture],
    ['zirconium',       createZirconiumTexture],
    ['steel',           createSteelTexture],
    ['structuralalloy', createStructuralAlloyTexture],
]);
